// Feature Flags Service - Client-Side Implementation
// Handles communication with Supabase for feature flag evaluation and management

import { supabase } from '@/lib/supabase';
import { FEATURE_FLAGS, BUILD_TIME_FLAGS } from '@/config/featureFlags';
import { retry } from '@/utils/retry';

class FeatureFlagsService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get user segments based on email and Supabase data
   */
  async getUserSegments(email) {
    const segments = new Set();
    if (!email) return segments;

    const lowerEmail = email.toLowerCase();
    const domain = lowerEmail.split('@')[1];

    // Admin segment
    if (domain === 'campconnect.app') {
      segments.add('admin');
    }

    // Beta segment - gracefully handle errors if table doesn't exist
    try {
      const { data: betaUser, error } = await supabase
        .from('beta_users')
        .select('status')
        .eq('email', lowerEmail)
        .maybeSingle();
      
      // Only check status if no error and data exists
      if (!error && betaUser?.status === 'approved') {
        segments.add('beta');
      }
    } catch (error) {
      // Silently fail if table doesn't exist or network error
      // This prevents console errors for missing tables
      console.debug('Could not check beta_users table:', error.message);
    }

    // Premium segment
    // TODO: Re-enable plan tier checking when column is added to database
    // const { data: profile } = await supabase
    //   .from('profiles')
    //   .select('plan_tier')
    //   .eq('email', lowerEmail)
    //   .maybeSingle();
    
    // if (profile?.plan_tier === 'premium') {
    //   segments.add('premium');
    // } else {
    //   segments.add('free');
    // }

    return segments;
  }

  /**
   * Evaluate feature flags for the current user
   */
  async evaluateFlags(flagKeys = []) {
    try {
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      const email = session?.user?.email;
      
      // If no specific flags requested, evaluate all
      const keysToEvaluate = flagKeys.length > 0 
        ? flagKeys 
        : Object.keys(FEATURE_FLAGS);

      const result = {};

      // Get user segments
      const segments = await this.getUserSegments(email);
      const userId = session?.user?.id;

      const retryOptions = { retries: 3, delay: 500 };

      // Get all flags from Supabase - gracefully handle errors
      let flags = [];
      try {
        const { data, error } = await retry(
          () => supabase
            .from('feature_flags')
            .select('key, default_enabled')
            .in('key', keysToEvaluate),
          retryOptions
        );
        if (!error && data) {
          flags = data;
        } else if (error) {
          console.debug('Could not load feature_flags table:', error.message);
        }
      } catch (error) {
        console.debug('Could not load feature_flags table:', error.message);
      }

      // Get segment rules - gracefully handle errors
      let segmentRules = [];
      try {
        const { data, error } = await retry(
          () => supabase
            .from('feature_segment_rules')
            .select('feature_key, segment, enabled')
            .in('feature_key', keysToEvaluate),
          retryOptions
        );
        if (!error && data) {
          segmentRules = data;
        } else if (error) {
          console.debug('Could not load feature_segment_rules table:', error.message);
        }
      } catch (error) {
        console.debug('Could not load feature_segment_rules table:', error.message);
      }

      // Get user overrides - gracefully handle errors
      let userOverrides = [];
      if (userId) {
        try {
          const { data, error } = await retry(
            () => supabase
              .from('feature_user_overrides')
              .select('feature_key, enabled')
              .eq('user_id', userId)
              .in('feature_key', keysToEvaluate),
            retryOptions
          );
          if (!error && data) {
            userOverrides = data;
          } else if (error) {
            console.debug('Could not load feature_user_overrides table:', error.message);
          }
        } catch (error) {
          console.debug('Could not load feature_user_overrides table:', error.message);
        }
      }

      // Evaluate each flag
      for (const key of keysToEvaluate) {
        // Start with build-time override if exists
        let enabled = BUILD_TIME_FLAGS[key];
        
        // If no build-time override, check database
        if (enabled === undefined) {
          const flag = flags?.find(f => f.key === key);
          enabled = flag ? !!flag.default_enabled : false;

          // Apply segment rules
          const rules = segmentRules?.filter(r => r.feature_key === key) || [];
          for (const rule of rules) {
            if (segments.has(rule.segment)) {
              enabled = rule.enabled;
            }
          }

          // Apply user override (highest priority)
          const override = userOverrides.find(o => o.feature_key === key);
          if (override) {
            enabled = override.enabled;
          }
        }

        result[key] = enabled;
      }

      return result;
    } catch (error) {
      console.error('Failed to evaluate feature flags:', error);
      // Return build-time flags as fallback
      return flagKeys.reduce((acc, key) => ({
        ...acc,
        [key]: BUILD_TIME_FLAGS[key] || false
      }), {});
    }
  }

  /**
   * Get all feature flags (admin only)
   */
  async getAllFlags() {
    let flags = [];
    try {
      const { data, error } = await retry(
        () => supabase
          .from('feature_flags')
          .select('*')
          .order('key'),
        { retries: 3, delay: 500 }
      );
      if (!error && data) {
        flags = data;
      }
    } catch (error) {
      console.debug('Could not load feature_flags table:', error.message);
    }
    
    let rules = [];
    try {
      const { data, error } = await retry(
        () => supabase
          .from('feature_segment_rules')
          .select('*'),
        { retries: 3, delay: 500 }
      );
      if (!error && data) {
        rules = data;
      }
    } catch (error) {
      console.debug('Could not load feature_segment_rules table:', error.message);
    }

    const flagsWithRules = (flags || []).map(flag => ({
      id: flag.key,
      key: flag.key,
      name: flag.name || flag.key,
      description: flag.description || '',
      enabled: flag.default_enabled,
      defaultValue: flag.default_enabled,
      segments: (rules || [])
        .filter(r => r.feature_key === flag.key)
        .map(r => r.segment)
    }));

    return flagsWithRules;
  }

  /**
   * Create a new feature flag (admin only)
   */
  async createFlag(flagData) {
    const key = flagData.name.toLowerCase().replace(/\s+/g, '_');
    
    const { data, error } = await supabase
      .from('feature_flags')
      .upsert({
        key,
        name: flagData.name,
        description: flagData.description || '',
        default_enabled: flagData.enabled !== undefined ? flagData.enabled : flagData.defaultValue
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }

  /**
   * Update a feature flag (admin only)
   */
  async updateFlag(flagId, updates) {
    const { data, error } = await supabase
      .from('feature_flags')
      .update({
        name: updates.name,
        description: updates.description,
        default_enabled: updates.enabled !== undefined ? updates.enabled : updates.defaultValue
      })
      .eq('key', flagId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }

  /**
   * Delete a feature flag (admin only)
   */
  async deleteFlag(flagId) {
    // Delete related records first
    await supabase.from('feature_segment_rules').delete().eq('feature_key', flagId);
    await supabase.from('feature_user_overrides').delete().eq('feature_key', flagId);
    
    const { error } = await supabase
      .from('feature_flags')
      .delete()
      .eq('key', flagId);
    
    if (error) throw error;
    return { success: true };
  }

  /**
   * Get all segments (admin only)
   */
  async getSegments() {
    try {
      const { data, error } = await supabase
        .from('feature_segment_rules')
        .select('*');
      
      if (!error && data) {
        return data;
      }
    } catch (error) {
      console.debug('Could not load feature_segment_rules table:', error.message);
    }
    return [];
  }

  /**
   * Get all user overrides (admin only)
   */
  async getUserOverrides() {
    try {
      const { data, error } = await supabase
        .from('feature_user_overrides')
        .select('*');
      
      if (!error && data) {
        return data;
      }
    } catch (error) {
      console.debug('Could not load feature_user_overrides table:', error.message);
    }
    return [];
  }

  /**
   * Set user override (admin only)
   */
  async setUserOverride(userId, flagId, enabled) {
    const { data, error } = await supabase
      .from('feature_user_overrides')
      .upsert({
        user_id: userId,
        feature_key: flagId,
        enabled: !!enabled
      }, {
        onConflict: 'user_id,feature_key'
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
}

export const featureFlagsService = new FeatureFlagsService();
